import { Alert, Button } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Page, PasswordResponses, Document as PDFDocument } from 'react-pdf';
import { useBoolean } from 'usehooks-ts';

import { Icon } from '@/components/Icon/Icon';
import { Loader } from '@/components/Icon/Loader';

import { useTranslate } from '@/hooks/useTranslate';

import { getDataTestId } from '@/utils/browser';

import { PDFContainer, ViewerLoader } from './File.styled';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

type PDFRendererProps = {
  file: string | File;
  pageLimit?: number;
  renderTextLayer?: boolean;
  onLoaded?: () => void;
};

type OnPasswordCallback = (password: string) => void;

export function PDFRenderer({
  file,
  renderTextLayer,
  pageLimit = 0,
  onLoaded,
}: PDFRendererProps) {
  const [numPages, setNumPages] = useState<number>();
  const [loadedPages, setLoadedPages] = useState<number>(0);
  const [containerWidth, setContainerWidth] = useState<number>();
  const { value: showPDF, toggle: toggleShowPDF } = useBoolean(true);
  const containerRef = useRef<HTMLElement>(null);

  const { t } = useTranslate();

  useEffect(() => {
    if (containerRef.current) {
      const { width } = containerRef.current.getBoundingClientRect();
      const { paddingLeft, paddingRight } = getComputedStyle(
        containerRef.current,
      );
      setContainerWidth(width - parseInt(paddingLeft) - parseInt(paddingRight));
    }
  }, [containerRef]);

  useEffect(() => {
    if (typeof numPages === 'number' && loadedPages >= numPages && onLoaded) {
      onLoaded();
    }
  }, [loadedPages, numPages, onLoaded]);

  const onDocumentLoadSuccess = ({
    numPages: nextNumPages,
  }: {
    numPages: number;
  }) => {
    setNumPages(pageLimit ? Math.min(nextNumPages, pageLimit) : nextNumPages);
  };

  const onPageLoaded = () => {
    setLoadedPages(prev => prev + 1);
  };

  function onPassword(callback: OnPasswordCallback, reason: number) {
    function callbackProxy(password: string | null) {
      // Cancel button handler
      if (password === null) {
        toggleShowPDF();
        return;
      }

      callback(password);
    }
    switch (reason) {
      case PasswordResponses.NEED_PASSWORD: {
        const password = prompt(t('pdfViewer.errorMessage.passwordRequired'));
        callbackProxy(password);
        break;
      }
      case PasswordResponses.INCORRECT_PASSWORD: {
        const password = prompt(t('pdfViewer.errorMessage.invalidPassword'));
        callbackProxy(password);
        break;
      }
      default:
    }
  }

  return (
    <PDFContainer
      ref={containerRef}
      className="PDFContainer"
      {...getDataTestId('pdf-renderer')}
    >
      {showPDF ? (
        <PDFDocument
          file={file}
          onPassword={onPassword}
          externalLinkTarget="_blank"
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<Loader />}
          error={
            <Alert
              icon={<Icon name="alert" />}
              color="error"
              variant="standard"
            >
              {t('pdfViewer.errorMessage.failToLoad')}
            </Alert>
          }
        >
          {Array.from(new Array(numPages), (_, index) => (
            <Page
              key={`page-${index}`}
              renderTextLayer={renderTextLayer}
              renderAnnotationLayer={renderTextLayer}
              pageNumber={index + 1}
              width={containerWidth}
              onLoadSuccess={onPageLoaded}
              loading={null}
            />
          ))}
        </PDFDocument>
      ) : (
        <ViewerLoader>
          <Button color="error" onClick={toggleShowPDF}>
            {t('common.retry')}
          </Button>
        </ViewerLoader>
      )}
    </PDFContainer>
  );
}
